/**
 * Waiting room - shows players before game starts
 */

import { useState } from 'react';
import { socketActions } from '../../lib/socketActions';
import { useGameStore } from '../../stores/gameStore';
import { PlayerAvatar } from '../game/PlayerAvatar';
import { getPlayerColor } from '../../utils/playerColors';
import { BotIcon } from '../ui/BotIcon';
import { DifficultySlider } from '../ui/DifficultySlider';
import { GameSettingsPanel } from './GameSettingsPanel';

export function WaitingRoom() {
  const { startGame, leaveRoom, addAI, removeAI, updateAIDifficulty, updateGameSettings } = socketActions;
  const gameState = useGameStore((s) => s.gameState);
  const playerId = useGameStore((s) => s.playerId);
  const roomCode = useGameStore((s) => s.roomCode);
  const addToast = useGameStore((s) => s.addToast);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  if (!gameState) return null;

  const myPlayer = gameState.players.find((p) => p.id === playerId);
  const isHost = myPlayer?.isHost ?? false;
  // Only count connected players for game start
  const connectedPlayers = gameState.players.filter((p) => p.isConnected);
  const canStart = connectedPlayers.length >= 2;
  const canAddAI = gameState.players.length < 8;

  const copyRoomCode = async () => {
    if (!navigator.clipboard) {
      addToast({ type: 'error', message: 'Clipboard not available' });
      return;
    }
    try {
      await navigator.clipboard.writeText(roomCode || '');
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {
      addToast({ type: 'error', message: 'Failed to copy code' });
    }
  };

  const copyInviteLink = async () => {
    if (!navigator.clipboard) {
      addToast({ type: 'error', message: 'Clipboard not available' });
      return;
    }
    try {
      const url = `${window.location.origin}?room=${roomCode}`;
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      addToast({ type: 'error', message: 'Failed to copy link' });
    }
  };

  return (
    <div className="min-h-content flex flex-col items-center justify-center p-4 safe-area-x">
      <div className="w-full max-w-md">
        {/* Room code display */}
        <div className="text-center mb-8">
          <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">Room Code</div>
          <button
            onClick={copyRoomCode}
            className={`text-4xl font-mono font-bold tracking-wider transition-colors ${
              copiedCode ? 'text-green-500' : 'text-blue-500 hover:text-blue-600'
            }`}
            title="Click to copy code"
          >
            {copiedCode ? 'Copied!' : roomCode}
          </button>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Share this code with friends to join
          </div>
          <button
            onClick={copyInviteLink}
            className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              copiedLink
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50'
            }`}
          >
            {copiedLink ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
            {copiedLink ? 'Copied!' : 'Copy Invite Link'}
          </button>
        </div>

        {/* Players list */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-6">
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-700 dark:text-slate-200">
              Players ({connectedPlayers.length}/{gameState.players.length > connectedPlayers.length ? `${gameState.players.length} total, ` : ''}8 max)
            </h3>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {gameState.players.map((player) => {
              const playerColor = getPlayerColor(player.colorIndex);
              const isDisconnected = !player.isConnected;
              return (
                <div
                  key={player.id}
                  className={`px-4 py-3 border-l-4 ${playerColor.border} ${isDisconnected ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <PlayerAvatar
                          name={player.name}
                          colorIndex={player.colorIndex}
                          size="md"
                          isCurrentUser={player.id === playerId}
                          isAI={player.isAI}
                        />
                        {player.isAI && (
                          <div className="absolute -bottom-0.5 -right-0.5 bg-slate-700 dark:bg-slate-600 rounded-full p-0.5">
                            <BotIcon size="sm" className="text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900 dark:text-white">
                          {player.name}
                          {player.id === playerId && (
                            <span className="text-slate-400 dark:text-slate-500"> (you)</span>
                          )}
                          {isDisconnected && (
                            <span className="text-slate-400 dark:text-slate-500"> (disconnected)</span>
                          )}
                        </span>
                        {player.isAI && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            Level {player.aiDifficulty ?? 5}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {player.isHost && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded">
                          Host
                        </span>
                      )}
                      {player.isAI && isHost && (
                        <button
                          onClick={() => removeAI(player.id)}
                          className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded transition-colors"
                          title="Remove AI player"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {/* AI difficulty slider - only show for host */}
                  {player.isAI && isHost && (
                    <div className="mt-3 pl-11">
                      <DifficultySlider
                        value={player.aiDifficulty ?? 5}
                        onChange={(difficulty) => updateAIDifficulty(player.id, difficulty)}
                        compact
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add AI button - host only */}
          {isHost && canAddAI && (
            <button
              onClick={() => addAI(5)}
              className="w-full px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors border-t border-slate-100 dark:border-slate-700"
            >
              <BotIcon size="md" />
              Add AI Player
            </button>
          )}
        </div>

        {/* Game Settings */}
        <div className="mb-6">
          <GameSettingsPanel
            turnTimerMs={gameState.turnTimerMs}
            endTimerMs={gameState.endTimerMs ?? 120000}
            isHost={isHost}
            onUpdateSettings={updateGameSettings}
          />
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {isHost ? (
            <button
              onClick={() => startGame()}
              disabled={!canStart}
              className={`w-full py-4 font-semibold rounded-lg transition-colors ${
                canStart
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
              }`}
            >
              {canStart ? 'Start Game' : 'Waiting for players (2 min)'}
            </button>
          ) : (
            <div className="text-center py-4 text-slate-600 dark:text-slate-400">
              Waiting for host to start the game...
            </div>
          )}

          <button
            onClick={() => leaveRoom()}
            className="w-full py-2 text-red-500 hover:text-red-600 font-medium"
          >
            Leave Room
          </button>
        </div>

        {/* Game rules hint */}
        <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <h4 className="font-semibold text-slate-700 dark:text-slate-200 mb-2">Quick Rules</h4>
          <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
            <li>• Claim 4+ letter words from the middle pool</li>
            <li>• Steal others' words by rearranging + adding letters</li>
            <li>• First to claim wins - be fast!</li>
            <li>• Longer words = more points</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
